"use client";

import React, { useState, useMemo } from "react";
import { ArrowRight, Building2, House, LayoutGrid, Loader } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { cn } from "@/lib/utils";
const stripHtml = (html: string) => {
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};
function ProjectsConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  const [activeTab, setActiveTab] = useState("all");
  const filteredPosts = useMemo(() => {
    if (activeTab === "all") return posts;
    return posts.filter(post => {
      return post.categories?.some(cat => cat.name.toLowerCase().includes(activeTab.toLowerCase()) || cat.slug.toLowerCase().includes(activeTab.toLowerCase()));
    });
  }, [posts, activeTab]);
  if (loading) {
    return <div className="flex min-h-[400px] w-full items-center justify-center">
        <Loader className="text-primary h-8 w-8 animate-spin" />
      </div>;
  }
  if (error) {
    return <div className="flex min-h-[200px] flex-col items-center justify-center gap-4 text-center">
        <p className="text-destructive">Fehler beim Laden der Projekte.</p>
        <Button onClick={() => refetch()} variant="outline">
          Erneut versuchen
        </Button>
      </div>;
  }
  return <div className="w-full space-y-12">
      {}
      <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab} className="flex flex-col items-center">
        <TabsList className="bg-muted/50 h-auto p-1">
          <TabsTrigger value="all" className="data-[state=active]:bg-background data-[state=active]:text-foreground rounded-[2px] px-6 py-2 text-sm font-medium transition-all">
            <LayoutGrid className="mr-2 h-4 w-4" />
            Alle Projekte
          </TabsTrigger>
          <TabsTrigger value="wohnbau" className="data-[state=active]:bg-background data-[state=active]:text-foreground rounded-[2px] px-6 py-2 text-sm font-medium transition-all">
            <House className="mr-2 h-4 w-4" />
            Wohnbau
          </TabsTrigger>
          <TabsTrigger value="gewerbe" className="data-[state=active]:bg-background data-[state=active]:text-foreground rounded-[2px] px-6 py-2 text-sm font-medium transition-all">
            <Building2 className="mr-2 h-4 w-4" />
            Gewerbe
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {}
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {filteredPosts.length > 0 ? filteredPosts.map((post, i) => <Card key={post.id} data-index={i} className="group bg-card border-border overflow-hidden rounded-xl border transition-all hover:shadow-lg">
              <div className="relative">
                <AspectRatio ratio={16 / 9}>
                  <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1544806/img/generic_construction_project.png"} alt={post.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </AspectRatio>
                <div className="absolute top-4 right-4">
                  <Badge className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-[2px] px-3 py-1 font-bold uppercase tracking-wider shadow-sm">
                    {post.tags && post.tags.length > 0 ? post.tags[0].name : "Laufend"}
                  </Badge>
                </div>
              </div>

              <CardHeader className="space-y-2 p-6">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-sans text-xl font-bold leading-tight">
                    {post.title}
                  </CardTitle>
                </div>
                <CardDescription className="text-muted-foreground line-clamp-2 text-sm">
                  {post.excerpt ? stripHtml(post.excerpt) : "Ein weiteres erfolgreiches Projekt der Leci Bau GmbH & Co.KG."}
                </CardDescription>
              </CardHeader>

              <CardFooter className="p-6 pt-0">
                <Link to={post.link || "#"} className="w-full">
                  <Button variant="outline" className="border-input hover:bg-accent hover:text-accent-foreground w-full justify-between rounded-[2px] border-2 px-4 py-5 text-sm font-semibold uppercase tracking-wide transition-colors">
                    Details ansehen
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardFooter>
            </Card>) : <div className="col-span-full py-12 text-center">
            <p className="text-muted-foreground text-lg">Keine Projekte in dieser Kategorie gefunden.</p>
            <Button variant="link" onClick={() => setActiveTab("all")} className="mt-2">
              Alle Projekte anzeigen
            </Button>
          </div>}
      </div>
    </div>;
}
export default function Projects() {
  const wp_query_params = {
    post_type: "post",
    per_page: 9,
    orderby: "date",
    order: "desc",
    status: "publish"
  };
  const wp_query = new WP_Query(wp_query_params);
  return null;
}